import { create } from "zustand";
import {
  MovieDetailItem,
  PopularParams,
  SearchParams,
  GetMovieDetailParams,
  Movie,
  TmdbImage,
  CastMember,
  MovieWatchProvidersResponse,
} from "../utils/types";
import {
  getMovieDetail,
  getMovieImages,
  getMovieTrailer,
  getPopularMovies,
  getRecommendedMovies,
  getSearchedMovies,
  getTopRatedMovies,
  getUpcomingMovies,
  getMovieCast,
  getMovieWatchProviders,
} from "../service/api";

interface MoviesState {
  popularMovies: Movie[];
  searchedMovies: Movie[];
  topRatedMovies: Movie[];
  upcomingMovies: Movie[];
  recommendedMovies: Movie[];
  searchQuery: string;
  hasMorePopular: boolean;
  hasMoreSearched: boolean;
  hasMoreTopRated: boolean;
  hasUpComingMovies: boolean;
  movieDetail: MovieDetailItem | null;
  movieImages: TmdbImage[];
  movieCast: CastMember[];
  watchProviders: MovieWatchProvidersResponse | null;
  isLoading: boolean;
  fetchPopularMovies: (params: PopularParams) => void;
  fetchUpcomingMovies: (params: PopularParams) => void;
  fetchSearchedMovies: (params: SearchParams) => void;
  fetchMovieDetail: (params: GetMovieDetailParams) => void;
  fetchMovieImages: (params: GetMovieDetailParams) => void;
  fetchMovieCast: (params: GetMovieDetailParams) => void;
  fetchMovieWatchProviders: (params: GetMovieDetailParams) => void;
  fetchTopRatedMovies: (params: PopularParams) => void;
  fetchRecommendedMovies: (params: GetMovieDetailParams) => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  resetMovieDetail: () => void;
}

export const useMoviesStore = create<MoviesState>((set) => ({
  popularMovies: [],
  searchedMovies: [],
  topRatedMovies: [],
  upcomingMovies: [],
  recommendedMovies: [],
  searchQuery: "",
  hasMorePopular: true,
  hasMoreSearched: true,
  hasMoreTopRated: true,
  hasUpComingMovies: true,
  movieDetail: null,
  movieImages: [],
  movieCast: [],
  watchProviders: null,
  isLoading: false,
  setSearchQuery: (query: string) =>
    set((state) => ({ ...state, searchQuery: query })),
  clearSearch: () =>
    set((state) => ({
      ...state,
      searchQuery: "",
      searchedMovies: [],
      hasMoreSearched: true,
    })),
  resetMovieDetail: () =>
    set((state) => ({
      ...state,
      movieDetail: null,
      movieImages: [],
      recommendedMovies: [],
      movieCast: [],
      watchProviders: null,
    })),
  fetchPopularMovies: async (params: PopularParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getPopularMovies(params);
    if (res != null) {
      set((state) => ({
        ...state,
        popularMovies:
          params.page === 1
            ? res.results
            : [...state.popularMovies, ...res.results],
        hasMorePopular: params.page < res.total_pages,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchUpcomingMovies: async (params: PopularParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getUpcomingMovies(params);
    if (res != null) {
      set((state) => ({
        ...state,
        upcomingMovies:
          params.page === 1
            ? res.results
            : [...state.upcomingMovies, ...res.results],
        hasUpComingMovies: params.page < res.total_pages,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchSearchedMovies: async (params: SearchParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getSearchedMovies(params);
    if (res != null) {
      set((state) => ({
        ...state,
        searchedMovies:
          params.page === 1
            ? [...res.results]
            : [...state.searchedMovies, ...res.results],
        hasMoreSearched: params.page < res.total_pages,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchMovieDetail: async (params: GetMovieDetailParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getMovieDetail(params);
    const trailer = await getMovieTrailer(params);
    if (res != null) {
      set((state) => ({
        ...state,
        movieDetail: { ...res, trailer },
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchMovieImages: async (params: GetMovieDetailParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getMovieImages(params);
    if (res != null) {
      set((state) => ({
        ...state,
        movieImages: res.posters,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchTopRatedMovies: async (params: PopularParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getTopRatedMovies(params);
    if (res != null) {
      set((state) => ({
        ...state,
        topRatedMovies:
          params.page === 1
            ? res.results
            : [...state.topRatedMovies, ...res.results],
        hasMoreTopRated: params.page < res.total_pages,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchRecommendedMovies: async (params: GetMovieDetailParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getRecommendedMovies(params);
    if (res != null) {
      set((state) => ({
        ...state,
        recommendedMovies: res.results,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchMovieCast: async (params: GetMovieDetailParams) => {
    set((state) => ({ ...state, isLoading: true }));
    const res = await getMovieCast(params);
    if (res != null) {
      set((state) => ({
        ...state,
        movieCast: res.cast,
        isLoading: false,
      }));
    } else {
      set((state) => ({ ...state, isLoading: false }));
    }
  },
  fetchMovieWatchProviders: async (params: GetMovieDetailParams) => {
    const res = await getMovieWatchProviders(params);
    if (res != null) {
      set((state) => ({
        ...state,
        watchProviders: res,
      }));
    }
  },
}));
